import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  IconButton,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Chip,
  SelectChangeEvent,
  Alert,
  Tooltip,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import Timeline from '@mui/lab/Timeline';
import TimelineItem, { timelineItemClasses } from '@mui/lab/TimelineItem';
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import TimelineConnector from '@mui/lab/TimelineConnector';
import TimelineContent from '@mui/lab/TimelineContent';
import TimelineDot from '@mui/lab/TimelineDot';
import { format, formatDistanceToNow } from 'date-fns';
import FileOpenIcon from '@mui/icons-material/FileOpen';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ShareIcon from '@mui/icons-material/Share';
import DriveFileMoveIcon from '@mui/icons-material/DriveFileMove';
import LoginIcon from '@mui/icons-material/Login';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import PermContactCalendarIcon from '@mui/icons-material/PermContactCalendar';
import RefreshIcon from '@mui/icons-material/Refresh';
import FilterListIcon from '@mui/icons-material/FilterList';
import GetAppIcon from '@mui/icons-material/GetApp';
import CreateNewFolderIcon from '@mui/icons-material/CreateNewFolder';
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';
import ClearIcon from '@mui/icons-material/Clear';

import activityService, { ActivityResponse, ActivityType } from '../../services/activityService';
import { Activity } from '../../types';
import EmptyState from '../ui/EmptyState';

type ActivityColor = 'primary' | 'secondary' | 'success' | 'info' | 'warning' | 'error';

interface ActivityTimelineProps {
  userId?: string;
  resourceId?: string;
  resourceType?: 'file' | 'folder';
  maxItems?: number;
  showFilters?: boolean;
  height?: string | number;
}

const ACTIVITY_TYPE_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: 'All activities' },
  { value: 'file_upload', label: 'Upload' },
  { value: 'file_download', label: 'Download' },
  { value: 'file_delete', label: 'Delete' },
  { value: 'file_share', label: 'Share' },
  { value: 'file_move', label: 'Move' },
  { value: 'file_rename', label: 'Rename' },
  { value: 'folder_create', label: 'Folder create' },
  { value: 'folder_update', label: 'Folder update' },
  { value: 'folder_delete', label: 'Folder delete' },
  { value: 'share_create', label: 'Share create' },
  { value: 'share_delete', label: 'Share delete' },
  { value: 'user_login', label: 'Login' },
  { value: 'user_logout', label: 'Logout' },
];

const normalizeActivities = (response: ActivityResponse | any): ActivityResponse => {
  if (!response) {
    return { activities: [], total: 0, page: 1, pageSize: 20 };
  }
  if (Array.isArray(response.activities)) {
    return response;
  }
  if (Array.isArray(response)) {
    return { activities: response, total: response.length, page: 1, pageSize: response.length };
  }
  if (Array.isArray(response.data)) {
    return {
      activities: response.data,
      total: response.pagination?.total ?? response.data.length,
      page: response.pagination?.page ?? 1,
      pageSize: response.pagination?.limit ?? response.data.length,
    };
  }
  if (Array.isArray(response.logs)) {
    return {
      activities: response.logs,
      total: response.total ?? response.logs.length,
      page: response.page ?? 1,
      pageSize: response.limit ?? response.logs.length,
    };
  }
  return { activities: [], total: 0, page: 1, pageSize: 20 };
};

const ActivityTimeline: React.FC<ActivityTimelineProps> = ({
  userId,
  resourceId,
  resourceType,
  maxItems = 25,
  showFilters = true,
  height = 560,
}) => {
  const [activities, setActivities] = useState<Activity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const [pagination, setPagination] = useState({
    total: 0,
    pages: 0,
    page: 1,
    limit: maxItems,
  });

  const [filters, setFilters] = useState({
    type: '',
    startDate: null as Date | null,
    endDate: null as Date | null,
  });

  const fetchActivities = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const startDateStr = filters.startDate
        ? format(filters.startDate, 'yyyy-MM-dd')
        : undefined;
      const endDateStr = filters.endDate
        ? format(filters.endDate, 'yyyy-MM-dd')
        : undefined;
      const typeFilter = (filters.type || undefined) as ActivityType | undefined;

      let raw: ActivityResponse | any;

      if (userId) {
        raw = await activityService.getUserActivities(
          userId,
          pagination.page,
          pagination.limit,
          filters.type || undefined,
          startDateStr,
          endDateStr
        );
      } else if (resourceId && resourceType) {
        raw = await activityService.getResourceActivities(
          resourceType,
          resourceId,
          pagination.page,
          pagination.limit
        );
      } else {
        raw = await activityService.getActivities(
          pagination.page,
          pagination.limit,
          typeFilter,
          startDateStr,
          endDateStr
        );
      }

      const response = normalizeActivities(raw);
      const list = (response.activities || []).map((item: any, idx: number) => ({
        ...item,
        id: item.id || item._id || `activity-${idx}`,
        type: item.type || item.action || 'activity',
        time: item.time || item.timestamp || item.createdAt || '',
      }));

      setActivities(list);
      setPagination((prev) => ({
        ...prev,
        page: response.page || prev.page,
        limit: response.pageSize || prev.limit,
        total: response.total || 0,
        pages: Math.max(1, Math.ceil((response.total || 0) / (response.pageSize || prev.limit || 1))),
      }));
    } catch {
      setError('Failed to load activities. Please try again.');
      setActivities([]);
    } finally {
      setLoading(false);
    }
  }, [
    userId,
    resourceId,
    resourceType,
    pagination.page,
    pagination.limit,
    filters.type,
    filters.startDate,
    filters.endDate,
  ]);

  useEffect(() => {
    fetchActivities();
  }, [fetchActivities]);

  const handleTypeChange = (event: SelectChangeEvent<string>) => {
    setFilters((prev) => ({ ...prev, type: event.target.value }));
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleDateChange = (field: 'startDate' | 'endDate') => (date: Date | null) => {
    setFilters((prev) => ({ ...prev, [field]: date }));
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleClearFilters = () => {
    setFilters({ type: '', startDate: null, endDate: null });
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleLoadMore = () => {
    setPagination((prev) => ({
      ...prev,
      page: prev.page + 1,
      limit: prev.limit + maxItems,
    }));
  };

  const handleExport = async () => {
    try {
      const startDateStr = filters.startDate
        ? format(filters.startDate, 'yyyy-MM-dd')
        : undefined;
      const endDateStr = filters.endDate
        ? format(filters.endDate, 'yyyy-MM-dd')
        : undefined;

      const blob = await activityService.exportActivities(
        'csv',
        filters.type || undefined,
        startDateStr,
        endDateStr
      );

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `activity-export-${format(new Date(), 'yyyy-MM-dd')}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      setError('Failed to export activities');
    }
  };

  const getActivityIcon = (type: string) => {
    const key = (type || '').toLowerCase();
    if (key.includes('upload') || key.includes('create')) return <UploadFileIcon fontSize="small" />;
    if (key.includes('download')) return <DownloadIcon fontSize="small" />;
    if (key.includes('delete')) return <DeleteIcon fontSize="small" />;
    if (key.includes('edit') || key.includes('update')) return <EditIcon fontSize="small" />;
    if (key.includes('share')) return <ShareIcon fontSize="small" />;
    if (key.includes('move')) return <DriveFileMoveIcon fontSize="small" />;
    if (key.includes('rename')) return <DriveFileRenameOutlineIcon fontSize="small" />;
    if (key.includes('folder')) return <CreateNewFolderIcon fontSize="small" />;
    if (key.includes('login') || key.includes('logout')) return <LoginIcon fontSize="small" />;
    if (key.includes('workflow')) return <AutoAwesomeIcon fontSize="small" />;
    if (key.includes('permission')) return <PermContactCalendarIcon fontSize="small" />;
    return <FileOpenIcon fontSize="small" />;
  };

  const getActivityColor = (type: string): ActivityColor => {
    const key = (type || '').toLowerCase();
    if (key.includes('upload') || key.includes('create')) return 'success';
    if (key.includes('download')) return 'info';
    if (key.includes('delete')) return 'error';
    if (key.includes('share')) return 'secondary';
    if (key.includes('move') || key.includes('rename')) return 'warning';
    return 'primary';
  };

  const formatActivityMessage = (activity: Activity): string => {
    type ActivityUserShape = {
      firstName?: string;
      lastName?: string;
      name?: string;
      email?: string;
    };

    const activityData = activity as Omit<Activity, 'user'> & {
      resourceName?: string;
      resourceType?: string;
      itemName?: string;
      details?: Record<string, any>;
      metadata?: Record<string, any>;
      user?: string | ActivityUserShape;
    };

    const userField = activityData.user;
    let userName = 'A user';
    if (typeof userField === 'string') {
      userName = userField.trim() || userName;
    } else if (userField && typeof userField === 'object') {
      userName =
        userField.name ||
        [userField.firstName, userField.lastName].filter(Boolean).join(' ').trim() ||
        userField.email ||
        userName;
    }

    userName =
      userName ||
      activityData.details?.userName ||
      activityData.metadata?.userName ||
      'A user';

    const rawType = (activityData.type || activityData.action || 'activity').toLowerCase();
    const actionWord = rawType
      .replace(/^file_/, '')
      .replace(/^folder_/, '')
      .replace(/^user_/, '')
      .replace(/^share_/, '')
      .replace(/_/g, ' ')
      .trim();

    const itemLabel =
      activityData.resourceName ||
      activityData.itemName ||
      activityData.item ||
      activityData.details?.resourceName ||
      activityData.details?.fileName ||
      activityData.details?.name ||
      activityData.resourceType ||
      'item';

    return `${userName} ${actionWord} ${itemLabel}`;
  };

  const formatActivityDate = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      if (Number.isNaN(date.getTime())) return dateString || '—';
      return format(date, 'MMM d, yyyy · h:mm a');
    } catch {
      return dateString || '—';
    }
  };

  const formatRelative = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      if (Number.isNaN(date.getTime())) return '';
      return formatDistanceToNow(date, { addSuffix: true });
    } catch {
      return '';
    }
  };

  const typeLabel = (type: string) =>
    (type || 'activity').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

  const hasActiveFilters = Boolean(filters.type || filters.startDate || filters.endDate);

  return (
    <Box sx={{ height, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={1.5}
        gap={1}
        flexWrap="wrap"
      >
        <Box>
          <Typography variant="subtitle1" fontWeight={600}>
            Timeline
          </Typography>
          {!loading && (
            <Typography variant="caption" color="text.secondary">
              {pagination.total.toLocaleString()} event{pagination.total === 1 ? '' : 's'}
            </Typography>
          )}
        </Box>
        <Stack direction="row" spacing={0.5} alignItems="center">
          {showFilters && (
            <Button
              size="small"
              variant={showFilterPanel || hasActiveFilters ? 'contained' : 'outlined'}
              startIcon={<FilterListIcon />}
              onClick={() => setShowFilterPanel((v) => !v)}
              sx={{ textTransform: 'none' }}
            >
              Filters{hasActiveFilters ? ' · on' : ''}
            </Button>
          )}
          <Tooltip title="Refresh">
            <IconButton onClick={fetchActivities} size="small" disabled={loading}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Export CSV">
            <IconButton onClick={handleExport} size="small">
              <GetAppIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {showFilters && showFilterPanel && (
        <Paper
          variant="outlined"
          sx={{ p: 1.5, mb: 1.5, bgcolor: 'background.default' }}
        >
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1.5}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            flexWrap="wrap"
          >
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel id="activity-type-label">Type</InputLabel>
              <Select
                labelId="activity-type-label"
                value={filters.type}
                label="Type"
                onChange={handleTypeChange}
              >
                {ACTIVITY_TYPE_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value || 'all'} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="From"
                value={filters.startDate}
                onChange={handleDateChange('startDate')}
                slotProps={{ textField: { size: 'small', sx: { minWidth: 140 } } }}
              />
              <DatePicker
                label="To"
                value={filters.endDate}
                onChange={handleDateChange('endDate')}
                slotProps={{ textField: { size: 'small', sx: { minWidth: 140 } } }}
              />
            </LocalizationProvider>

            <Button
              variant="text"
              size="small"
              startIcon={<ClearIcon />}
              onClick={handleClearFilters}
              disabled={!hasActiveFilters}
              sx={{ textTransform: 'none' }}
            >
              Clear
            </Button>
          </Stack>
        </Paper>
      )}

      <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
        {loading && activities.length === 0 ? (
          <Box display="flex" justifyContent="center" alignItems="center" height={200}>
            <CircularProgress size={32} />
          </Box>
        ) : activities.length === 0 ? (
          <EmptyState
            type="default"
            title="No activity yet"
            description={
              hasActiveFilters
                ? 'No events match your filters. Try clearing them or widening the date range.'
                : 'Uploads, shares, and other file events will show up here.'
            }
            actionText={hasActiveFilters ? 'Clear filters' : undefined}
            onAction={hasActiveFilters ? handleClearFilters : undefined}
          />
        ) : (
          <Timeline
            sx={{
              p: 0,
              m: 0,
              [`& .${timelineItemClasses.root}:before`]: {
                flex: 0,
                padding: 0,
              },
            }}
          >
            {activities.map((activity, index) => {
              const stamp = activity.time || (activity as any).timestamp || '';
              return (
                <TimelineItem key={activity.id} sx={{ minHeight: 56 }}>
                  <TimelineSeparator>
                    <TimelineDot
                      color={getActivityColor(activity.type)}
                      sx={{ boxShadow: 'none', my: 0.5 }}
                    >
                      {getActivityIcon(activity.type)}
                    </TimelineDot>
                    {index < activities.length - 1 && <TimelineConnector />}
                  </TimelineSeparator>
                  <TimelineContent sx={{ py: 0.75, px: 1.5 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        gap: 1,
                        alignItems: 'flex-start',
                        flexWrap: 'wrap',
                      }}
                    >
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography variant="body2" fontWeight={500} sx={{ lineHeight: 1.35 }}>
                          {formatActivityMessage(activity)}
                        </Typography>
                        <Stack direction="row" spacing={1} alignItems="center" mt={0.5} flexWrap="wrap">
                          <Chip
                            label={typeLabel(activity.type)}
                            size="small"
                            color={getActivityColor(activity.type)}
                            variant="outlined"
                            sx={{ height: 22, '& .MuiChip-label': { px: 0.75, fontSize: '0.7rem' } }}
                          />
                          <Typography variant="caption" color="text.secondary">
                            {formatActivityDate(stamp)}
                            {formatRelative(stamp) ? ` · ${formatRelative(stamp)}` : ''}
                          </Typography>
                        </Stack>
                      </Box>
                    </Box>
                  </TimelineContent>
                </TimelineItem>
              );
            })}
          </Timeline>
        )}
      </Box>

      {pagination.page < pagination.pages && activities.length > 0 && (
        <Box display="flex" justifyContent="center" mt={1.5}>
          <Button
            variant="outlined"
            size="small"
            onClick={handleLoadMore}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={14} /> : undefined}
            sx={{ textTransform: 'none' }}
          >
            Load more
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default ActivityTimeline;
